import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';
import { fetchCollection, getItemSlug } from '../lib/cms';
import ArticleCard from '../components/ArticleCard';
import VideoFacade from '../components/VideoFacade';
import HelpNotice from '../components/HelpNotice';
import Ticker from '../components/Ticker';
import CategoryBadge from '../components/CategoryBadge';

export default function HomePage() {
  useSeo({
    title: 'Forside',
    description: 'Skatunderlup – Danmarks uafhængige digitale fagavis for skat, moms, afgifter og retssikkerhed for private og erhverv.',
  });

  const [articles, setArticles] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        const [artRes, medRes, topRes] = await Promise.all([
          fetchCollection('articles', { sort: 'publish_date', order: 'desc', limit: 12 }),
          fetchCollection('presse_film', { limit: 4 }),
          fetchCollection('om_skat', { limit: 6 }),
        ]);
        if (isMounted) {
          setArticles(artRes.items || []);
          setMediaItems(medRes.items || []);
          setTopics(topRes.items || []);
          setLoading(false);
        }
      } catch (e) {
        console.warn('Error loading homepage data', e);
        if (isMounted) setLoading(false);
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, []);

  // Segregate articles for the editorial layout
  const leadArticle = articles.find((a) => a.featured) || articles[0];
  const secondaryArticles = articles.filter((a) => a.id !== leadArticle?.id).slice(0, 2);
  const sidebarArticles = articles.filter((a) => a.id !== leadArticle?.id).slice(2, 6);
  const privateArticles = articles.filter((a) => a.audience === 'privat' || a.audience === 'begge').slice(0, 3);
  const erhvervArticles = articles.filter((a) => a.audience === 'erhverv' || a.audience === 'begge').slice(0, 3);
  const featuredVideo = mediaItems.find((m) => m.media_type === 'film') || mediaItems[0];

  return (
    <div className="min-h-screen bg-white">
      {/* Editorial Breaking / Focus Ticker */}
      <Ticker
        items={articles.slice(0, 4).map((a) => ({
          title: a.title,
          url: `/artikler/${getItemSlug(a)}`,
        }))}
      />

      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* SECTION 1: MAIN EDITORIAL GRID (Lead + Sublead + Sidebar) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pb-12 border-b border-slate-200">
          {/* Main 8-column editorial column: Lead story + 2 secondary stories */}
          <div className="lg:col-span-8 flex flex-col justify-between">
            {leadArticle && (
              <ArticleCard article={leadArticle} variant="lead" className="mb-8" />
            )}

            {/* 2-Column Secondary Stories */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
              {secondaryArticles.map((art) => (
                <ArticleCard key={art.id} article={art} variant="secondary" />
              ))}
            </div>
          </div>

          {/* Right 4-column column: Editorial sidebar (Latest, analysis, help notice) */}
          <aside className="lg:col-span-4 lg:border-l lg:border-slate-200 lg:pl-8 flex flex-col gap-6">
            <div className="border-b border-slate-200 pb-2 flex items-center justify-between">
              <h3 className="font-serif text-lg font-bold uppercase tracking-wider text-[#010b27]">
                Seneste fra redaktionen
              </h3>
              <span className="text-[10px] font-mono text-[#fd7302] uppercase tracking-wider font-bold">
                Opdateres løbende
              </span>
            </div>

            <div className="divide-y divide-slate-100">
              {sidebarArticles.map((art) => (
                <ArticleCard key={art.id} article={art} variant="compact" />
              ))}
            </div>

            {/* Editorial Help Box for Martin Krogh */}
            <HelpNotice variant="sidebar" />
          </aside>
        </div>

        {/* SECTION 2: AUDIENCE HIGHLIGHTS (Private & Erhverv side-by-side) */}
        <section className="py-12 border-b border-slate-200" aria-label="Sektioner for Private og Erhvervsdrivende">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Box: For Private */}
            <div className="rounded-md border border-slate-200 bg-slate-50/50 p-6 sm:p-8">
              <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-sm bg-[#010b27] text-white text-xs">
                    <i className="fa-solid fa-user" aria-hidden="true" />
                  </span>
                  <h3 className="font-serif text-2xl font-bold text-[#010b27]">
                    For Private
                  </h3>
                </div>
                <Link
                  to="/private"
                  className="text-xs font-bold uppercase tracking-wider text-[#010b27] hover:text-[#fd7302] inline-flex items-center gap-1 transition-colors"
                >
                  <span>Gå til sektion</span>
                  <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>

              <p className="mt-3 text-xs sm:text-sm text-slate-600 leading-relaxed">
                Journalistik og overblik over personlig indkomstskat, ejendomsvurderinger, boligskat, kørselsfradrag og årsopgørelsen.
              </p>

              <div className="mt-6 space-y-3">
                {privateArticles.map((art) => (
                  <Link
                    key={art.id}
                    to={`/artikler/${getItemSlug(art)}`}
                    className="group block rounded-sm p-2 -mx-2 hover:bg-slate-100/70 pb-3 border-b border-slate-200/80 last:border-0 transition-colors"
                  >
                    <span className="text-[10px] font-mono text-slate-500">
                      {art.publish_date}
                    </span>
                    <h4 className="font-serif text-base font-bold text-[#010b27] group-hover:text-[#fd7302] transition-colors leading-snug mt-0.5">
                      {art.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </div>

            {/* Right Box: For Erhvervsdrivende */}
            <div className="rounded-md border border-slate-200 bg-[#fffaf5] p-6 sm:p-8">
              <div className="flex items-center justify-between pb-3 border-b border-[#fd7302]/20">
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-sm bg-[#fd7302] text-white text-xs">
                    <i className="fa-solid fa-building" aria-hidden="true" />
                  </span>
                  <h3 className="font-serif text-2xl font-bold text-[#010b27]">
                    For Erhvervsdrivende
                  </h3>
                </div>
                <Link
                  to="/erhvervsdrivende"
                  className="text-xs font-bold uppercase tracking-wider text-[#fd7302] hover:underline inline-flex items-center gap-1"
                >
                  <span>Gå til sektion</span>
                  <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>

              <p className="mt-3 text-xs sm:text-sm text-slate-600 leading-relaxed">
                Dybdegående dækning af momsindberetning, selskabsskat, revisionstilsyn, lagerværdier og skattepolitiske reformer for danske SMV'er.
              </p>

              <div className="mt-6 space-y-3">
                {erhvervArticles.map((art) => (
                  <Link
                    key={art.id}
                    to={`/artikler/${getItemSlug(art)}`}
                    className="group block rounded-sm p-2 -mx-2 hover:bg-[#fd7302]/10 pb-3 border-b border-[#fd7302]/15 last:border-0 transition-colors"
                  >
                    <span className="text-[10px] font-mono text-slate-500">
                      {art.publish_date}
                    </span>
                    <h4 className="font-serif text-base font-bold text-[#010b27] group-hover:text-[#fd7302] transition-colors leading-snug mt-0.5">
                      {art.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3: PRESSE & FILM MULTIMEDIA SPOTLIGHT */}
        {featuredVideo && (
          <section className="py-12 border-b border-slate-200" aria-label="Presse og Film Spotlight">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 pb-2 border-b border-slate-200">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#fd7302]">
                  Multimedia & Debat
                </span>
                <h3 className="font-serif text-3xl font-black text-[#010b27]">
                  Presse & Film
                </h3>
              </div>
              <Link
                to="/presse-og-film"
                className="mt-2 md:mt-0 text-xs font-bold uppercase tracking-wider text-[#fd7302] hover:underline inline-flex items-center gap-1"
              >
                <span>Se alle videoer og presseindhold</span>
                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
              </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <VideoFacade
                  videoUrl={featuredVideo.video_url}
                  coverImage={featuredVideo.cover_image}
                  title={featuredVideo.title}
                  duration={featuredVideo.duration}
                />
              </div>

              <div className="lg:col-span-5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="rounded-xs bg-[#fd7302] px-2 py-0.5 text-[10px] font-bold text-white uppercase tracking-wider">
                      Fremhævet analyse
                    </span>
                    <CategoryBadge audience={featuredVideo.audience} size="xs" />
                  </div>

                  <Link
                    to={`/presse-og-film/${featuredVideo.slug?._ || featuredVideo.slug || ''}`}
                    className="block font-serif text-2xl sm:text-3xl font-bold text-[#010b27] hover:text-[#fd7302] transition-colors leading-tight"
                  >
                    {featuredVideo.title}
                  </Link>

                  <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
                    {featuredVideo.description}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-200 flex items-center justify-between">
                  <span className="text-xs font-mono text-slate-500">
                    Varighed: {featuredVideo.duration || '14 min'}
                  </span>
                  <Link
                    to={`/presse-og-film/${featuredVideo.slug?._ || featuredVideo.slug || ''}`}
                    className="text-xs font-bold text-[#fd7302] hover:underline"
                  >
                    Læs om udsendelsen →
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* SECTION 4: OM SKAT (Vidensunivers emner) */}
        <section className="py-12 border-b border-slate-200" aria-label="Om Skat Vidensunivers">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 pb-2 border-b border-slate-200">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#fd7302]">
                Faglig oplysning
              </span>
              <h3 className="font-serif text-3xl font-black text-[#010b27]">
                Om skat: Vidensuniverset
              </h3>
            </div>
            <Link
              to="/om-skat"
              className="mt-2 sm:mt-0 text-xs font-bold uppercase tracking-wider text-[#fd7302] hover:underline inline-flex items-center gap-1"
            >
              <span>Udforsk alle emner</span>
              <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {topics.map((t) => (
              <Link
                key={t.id}
                to={`/om-skat/${t.slug?._ || t.slug || ''}`}
                className="group rounded-md border border-slate-200 bg-white p-5 transition-all hover:border-[#fd7302]/60 hover:shadow-xs focus:outline-none focus:ring-2 focus:ring-[#fd7302]"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#fd7302]">
                    {t.topic}
                  </span>
                  <CategoryBadge audience={t.audience} size="xs" />
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-slate-100 text-[#010b27] group-hover:bg-[#fd7302] group-hover:text-white transition-colors">
                    <i className={`${t.icon_name || 'fa-solid fa-file-lines'} text-sm`} aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-serif text-lg font-bold text-[#010b27] group-hover:text-[#fd7302] transition-colors leading-snug">
                      {t.title}
                    </h4>
                    <p className="mt-1.5 text-xs text-slate-600 line-clamp-2 leading-relaxed">
                      {t.summary}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* SECTION 5: FULL-WIDTH EDITORIAL CTA (Har brug for hjælp?) */}
        <section className="pt-12" aria-label="Redaktionel henvisning">
          <HelpNotice variant="banner" />
        </section>
      </div>
    </div>
  );
}
